function toTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function toObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function parseJsonObject(value) {
  if (typeof value !== "string") {
    return toObject(value);
  }

  const input = value.trim();
  if (!input) {
    return null;
  }

  try {
    return toObject(JSON.parse(input));
  } catch {
    return null;
  }
}

function pickString(...values) {
  for (const value of values) {
    const normalized = toTrimmedString(value);
    if (normalized) {
      return normalized;
    }
  }
  return "";
}

function getSubject(root, message) {
  return pickString(
    root?.subject,
    root?.topic,
    root?.channel,
    root?.subscription,
    root?.event,
    message?.subject,
    message?.topic,
    message?.channel,
  );
}

function getActionFromSubject(subject) {
  const normalizedSubject = toTrimmedString(subject).toLowerCase();
  if (!normalizedSubject.startsWith("appointments.")) {
    return "";
  }

  const [, ...rest] = normalizedSubject.split(".");
  return pickString(rest.join("."));
}

function isAppointmentMetadata(metadata) {
  const normalizedMetadata = parseJsonObject(metadata);
  if (!normalizedMetadata) {
    return false;
  }

  const objectType = pickString(
    normalizedMetadata.objectType,
    normalizedMetadata.object_type,
  ).toLowerCase();

  if (objectType === "appointment") {
    return true;
  }

  const systemScope = pickString(
    normalizedMetadata.systemScope,
    normalizedMetadata.system_scope,
  ).toLowerCase();

  return systemScope === "appointment";
}

function collectSources(root, message) {
  const parsedRootBody = parseJsonObject(root?.body);
  const parsedMessageBody = parseJsonObject(message?.body);

  return [
    parseJsonObject(root?.appointmentChange),
    parseJsonObject(root?.appointment_change),
    parseJsonObject(root?.appointment),
    parseJsonObject(root?.change),
    parseJsonObject(root?.payload),
    parsedRootBody,
    parseJsonObject(root?.payload?.body),
    message,
    parsedMessageBody,
    parseJsonObject(message?.payload),
    parseJsonObject(message?.payload?.body),
  ].filter(Boolean);
}

function looksLikeAppointmentContext(root, message, sources, subject) {
  if (toTrimmedString(subject).toLowerCase().startsWith("appointments.")) {
    return true;
  }

  if (isAppointmentMetadata(root?.metadata) || isAppointmentMetadata(message?.metadata)) {
    return true;
  }

  const rootType = pickString(root?.type, root?.kind).toLowerCase();
  if (rootType === "appointment" || rootType === "appointment_change") {
    return true;
  }

  const messageType = pickString(message?.type, message?.kind).toLowerCase();
  if (messageType === "appointment" || messageType === "appointment_change") {
    return true;
  }

  return sources.some((source) =>
    Boolean(
      pickString(
        source?.appointmentId,
        source?.appointmentID,
        source?.AppointmentID,
      ),
    ),
  );
}

export function normalizeAppointmentChangePayload(rawPayload) {
  const root = toObject(rawPayload);
  if (!root) {
    return null;
  }

  const message = toObject(root.message) || parseJsonObject(root.message);
  const subject = getSubject(root, message);
  const sources = collectSources(root, message);

  if (!looksLikeAppointmentContext(root, message, sources, subject)) {
    return null;
  }

  let action = getActionFromSubject(subject);
  let appointmentId = "";
  let tenantId = "";
  let patientId = "";
  let doctorId = "";
  let calendarId = "";
  let status = "";

  for (const source of sources) {
    action ||= pickString(source?.action, source?.Action);
    appointmentId ||= pickString(
      source?.appointmentId,
      source?.appointmentID,
      source?.AppointmentID,
      source?.id,
    );
    tenantId ||= pickString(source?.tenantId, source?.tenantID, source?.TenantID);
    patientId ||= pickString(source?.patientId, source?.patientID, source?.PatientID);
    doctorId ||= pickString(source?.doctorId, source?.doctorID, source?.DoctorID);
    // Standalone-calendar direct entries carry a calendarId but nil doctor/tenant ids;
    // the bridge's filter gate uses it to decide whether a refetch is warranted.
    calendarId ||= pickString(source?.calendarId, source?.calendarID, source?.CalendarID);
    status ||= pickString(source?.status, source?.Status);
  }

  if (!appointmentId && !action && !tenantId && !patientId && !doctorId && !calendarId && !status) {
    return null;
  }

  return {
    action: action.toLowerCase(),
    appointmentId,
    tenantId,
    patientId,
    doctorId,
    calendarId,
    status,
    subject: toTrimmedString(subject),
    raw: rawPayload,
  };
}

export function isAppointmentRealtimePayload(rawPayload) {
  return normalizeAppointmentChangePayload(rawPayload) !== null;
}
